import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Table as KSNMTable } from 'ksnm-common-ui/lib/Table2';
import { getLocalRowsByCheckBoxStatus } from 'ksnm-common-ui/lib/Table2/utils/getLocalRowsByCheckBoxStatus';
import { FILTER } from 'ksnm-common-ui/lib/Table2/utils/TableRequest/TableRequest.interface';
import type { Filter } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { RoutePath } from '@components/AppRouter/constants';
import { getExcelWithAudit } from '@src/utils/getExcelWithAudit';
import { useTableTitleWithCount } from '@src/utils/hooks/useTableTitleWithCount';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { objectFieldsToSnakeCase } from '@src/utils/objectFieldsToCamelCase';
import { constantStore } from '@stores/constantStore';
import { reportGroupsStore } from '@stores/dictionaryStores/reportGroupsStore/reportGroupsStore';
import { ksnmTableStore } from '@stores/ksnmTableStore/ksnmTableStore';

import { ActionButton } from './ActionButton';
import { columns } from './columns';

import styles from './Table.module.scss';

const { TABLE_NAMES } = constantStore;

export const Table = observer(() => {
  const { reportGroups, setChecked } = reportGroupsStore;
  const { push } = useHistory();

  const hardFilter = useMemo<Filter[][]>(
    () => [
      [
        {
          filterIsInverted: false,
          name: 'regulated',
          type: FILTER.EQ,
          values: [false],
        },
      ],
    ],
    [],
  );

  const [key, update, externalControl] = useUpdateTable({ tableName: TABLE_NAMES.DICTIONARY_REPORT_GROUPS });

  useEffect(() => {
    update();
  }, [update]);

  const onCheckBoxChange = useCallback(
    async (tr, sr) => {
      // TODO переделать когда, в таблицном компоненте на беке добавят запрос для получение выбранных строк
      const rows = await getLocalRowsByCheckBoxStatus({
        tableRequest: { ...tr },
        localData: {
          data: reportGroups.map((item) => objectFieldsToSnakeCase(item)),
          fieldFormat: {},
        },
        selectRows: sr,
        keyField: 'code',
      });

      setChecked(rows);
    },
    [reportGroups, setChecked],
  );

  const onRowClick = useCallback(
    (value) => {
      push(`${RoutePath.DICTIONARY_REPORT_GROUPS}/${value?.code}`);
    },
    [push],
  );

  const [titleWithCount, onTableChange] = useTableTitleWithCount(<></>);

  return (
    <div className={styles.table}>
      <KSNMTable
        externalControl={externalControl}
        tableName={TABLE_NAMES.DICTIONARY_REPORT_GROUPS}
        columns={columns}
        tableTitle={titleWithCount}
        hardFilter={hardFilter}
        resource="cm.report_group"
        externalFetch={ksnmTableStore.externalFetch}
        externalFetchXLSX={getExcelWithAudit('Справочник "Группы отчетности"')}
        onRowClick={onRowClick}
        onCheckBoxChange={onCheckBoxChange}
        extendTableButtons={<ActionButton update={update} />}
        showAggregateRow={false}
        key={key}
        keyField="code"
        onTableChange={onTableChange}
      />
    </div>
  );
});
